/**
 * Roadways Element Colors Store
 *
 * @module roadways/store
 * @description Store Zustand para persistir colores personalizados de elementos viales
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * IDs de elementos viales que soportan color personalizable
 */
export type RoadwaysElementId = 'road-segments' | 'road-defects';

/**
 * Colores default para elementos viales
 */
export const DEFAULT_ROADWAYS_COLORS: Record<RoadwaysElementId, string> = {
  'road-segments': '#64748B', // Gris pizarra
  'road-defects': '#F97316',  // Naranja (alerta)
};

/**
 * Estado del store
 */
interface RoadwaysElementColorsState {
  /**
   * Overrides de colores (solo se guardan los cambios del usuario)
   */
  colorOverrides: Partial<Record<RoadwaysElementId, string>>;

  /**
   * Obtiene el color actual de un elemento (override o default)
   */
  getColor: (elementId: RoadwaysElementId) => string;

  /**
   * Establece un color personalizado para un elemento
   */
  setColor: (elementId: RoadwaysElementId, color: string) => void;

  /**
   * Resetea el color de un elemento al default
   */
  resetColor: (elementId: RoadwaysElementId) => void;

  /**
   * Resetea todos los colores a defaults
   */
  resetAllColors: () => void;
}

/**
 * Store de colores de elementos viales
 *
 * @remarks
 * Usa persist middleware para guardar en localStorage.
 * Solo se guardan los overrides, fallback a defaults.
 */
export const useRoadwaysElementColorsStore = create<RoadwaysElementColorsState>()(
  persist(
    (set, get) => ({
      colorOverrides: {},

      getColor: (elementId): string => {
        const override = get().colorOverrides[elementId];
        return override || DEFAULT_ROADWAYS_COLORS[elementId];
      },

      setColor: (elementId, color): void => {
        set((state) => ({
          colorOverrides: {
            ...state.colorOverrides,
            [elementId]: color,
          },
        }));
      },

      resetColor: (elementId): void => {
        set((state) => {
          const newOverrides = { ...state.colorOverrides };
          delete newOverrides[elementId];
          return { colorOverrides: newOverrides };
        });
      },

      resetAllColors: (): void => {
        set({ colorOverrides: {} });
      },
    }),
    {
      name: 'roadways-element-colors',
      partialize: (state) => ({ colorOverrides: state.colorOverrides }),
    }
  )
);
